import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Ban, RefreshCw, Users, RotateCcw } from 'lucide-react';
import { hasPermission } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Requesters({ user }) {
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', department: '', contact: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const isManager = hasPermission(user, 'requesters.manage');

  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/requesters`, { params: { active_only: !showInactive } });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load requesters');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', department: '', contact: '', notes: '' });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, department: r.department || '', contact: r.contact || '', notes: r.notes || '' });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/requesters/${editing.id}`, form);
        toast.success('Requester updated');
      } else {
        await axios.post(`${API}/requesters`, form);
        toast.success('Requester created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save requester');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (r) => {
    if (!window.confirm(`Suspend '${r.name}'? They will no longer appear on the outbound movement dropdown. Historical records stay intact and you can reactivate them anytime.`)) return;
    try {
      await axios.delete(`${API}/requesters/${r.id}`);
      toast.success('Requester suspended');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const reactivate = async (r) => {
    try {
      await axios.patch(`${API}/requesters/${r.id}`, { active: true });
      toast.success('Requester reactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="requesters-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Users className="h-9 w-9 text-[#00a89c]" />
            Requesters
          </h1>
          <p className="text-slate-600 mt-2">
            Formalise the people and teams who receive internal materials. Selected on every outbound movement so consumption can be analysed by requester.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="toggle-show-inactive"
            />
            <Label htmlFor="show-inactive" className="text-sm text-slate-600">Show suspended</Label>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-requesters">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          {isManager && (
            <Button onClick={openCreate} className="bg-[#00a89c] hover:bg-[#008b82]" data-testid="add-requester-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add requester
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">
            {items.length} requester{items.length === 1 ? '' : 's'}
            {showInactive ? ' (including suspended)' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00a89c]"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No requesters yet.{' '}
              {isManager && (
                <button onClick={openCreate} className="text-[#00a89c] font-medium hover:underline">
                  Add the first one
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-4 font-medium">Name</th>
                    <th className="text-left py-2 pr-4 font-medium">Department</th>
                    <th className="text-left py-2 pr-4 font-medium">Contact</th>
                    <th className="text-left py-2 pr-4 font-medium">Status</th>
                    <th className="text-right py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`requester-row-${r.id}`}>
                      <td className="py-3 pr-4 font-medium text-slate-900">{r.name}</td>
                      <td className="py-3 pr-4 text-slate-600">{r.department || <span className="text-slate-400">—</span>}</td>
                      <td className="py-3 pr-4 text-slate-600 font-mono text-xs">{r.contact || <span className="text-slate-400">—</span>}</td>
                      <td className="py-3 pr-4">
                        {r.active ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700">Active</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Suspended</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {isManager && (
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit" data-testid={`edit-requester-${r.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {r.active ? (
                              <Button size="sm" variant="ghost" onClick={() => deactivate(r)} title="Suspend" data-testid={`deactivate-requester-${r.id}`}>
                                <Ban className="h-4 w-4 text-red-500" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => reactivate(r)} title="Reactivate" data-testid={`reactivate-requester-${r.id}`}>
                                <RotateCcw className="h-4 w-4 text-teal-600" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit requester' : 'Add requester'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update requester details.' : 'Register a new person or team that receives internal materials.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Kojo Mensah, Motor Bay 3"
                required
                data-testid="requester-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Department / team</Label>
              <Input
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                placeholder="e.g. Riders, Workshop, Ops"
                data-testid="requester-department-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="email or phone"
                data-testid="requester-contact-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything useful for audit or reporting"
                data-testid="requester-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#00a89c] hover:bg-[#008b82]"
                data-testid="submit-requester-btn"
              >
                {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
